import { useState, useEffect, useRef, useMemo } from 'react';
import { useTestStore, type TestId } from '@/store/useTestStore';
import { t } from '@/i18n/translations';
import { TestCard } from '@/components/TestCard';
import { MetricBox } from '@/components/MetricBox';
import { useFpsMeter } from '@/hooks/useFpsMeter';
import { scoreFromFps, qualityFromFps } from '@/utils/scoring';

interface Props { autoRun: boolean; autoNonce: number; }

export function ScrollTest({ autoRun, autoNonce }: Props) {
  const { language, reportResult, onAutoDone } = useTestStore();
  const [running, setRunning] = useState(false);
  const [scrollCount, setScrollCount] = useState(0);
  const fps = useFpsMeter(running);
  const containerRef = useRef<HTMLDivElement>(null);
  const autoDoneRef = useRef(false);
  const fpsHistory = useRef<number[]>([]);

  const items = useMemo(() => {
    const out = [];
    for (let i = 0; i < 500; i++) {
      out.push({
        id: i,
        title: `Item #${i + 1}`,
        desc: `Complex content row with multiple elements, gradients, and shadows — row ${i + 1}`,
        color: `hsl(${(i * 37) % 360}, 70%, 60%)`,
      });
    }
    return out;
  }, []);

  function reset() {
    setRunning(false);
    autoDoneRef.current = false;
    setScrollCount(0);
    fpsHistory.current = [];
  }

  function start() {
    reset();
    setRunning(true);
  }

  function stop() {
    setRunning(false);
    const avgFps = fpsHistory.current.length > 0
      ? fpsHistory.current.reduce((a, b) => a + b, 0) / fpsHistory.current.length
      : fps;
    const sc = Math.round(scoreFromFps(avgFps));
    const testId: TestId = 'mobile-scroll';
    reportResult(testId, {
      score: sc,
      primary: `Scroll FPS ${avgFps.toFixed(1)} · ${scrollCount} events`,
      details: { avgFps, scrollCount },
    });
    if (autoRun && !autoDoneRef.current) { autoDoneRef.current = true; onAutoDone(testId); }
  }

  useEffect(() => {
    if (running && fps > 0) fpsHistory.current.push(fps);
  }, [fps, running]);

  function onScroll() {
    if (!running) return;
    setScrollCount((c) => c + 1);
  }

  // AUTO-RUN: programmatic scroll
  useEffect(() => {
    if (!autoRun) return;
    start();
    const el = containerRef.current;
    let frame = 0;
    const maxFrames = 180; // ~3s at 60fps
    function scrollLoop() {
      if (!el || frame >= maxFrames) { stop(); return; }
      el.scrollTop += 8;
      if (el.scrollTop >= el.scrollHeight - el.clientHeight) el.scrollTop = 0;
      frame++;
      requestAnimationFrame(scrollLoop);
    }
    const timer = setTimeout(() => requestAnimationFrame(scrollLoop), 500);
    const stopTimer = setTimeout(() => stop(), 5000);
    return () => { clearTimeout(timer); clearTimeout(stopTimer); };
  }, [autoRun, autoNonce]);

  const q = qualityFromFps(fps);

  return (
    <TestCard title={t('mobile.scroll.title', language)} description={t('mobile.scroll.desc', language)}>
      <div className="flex flex-wrap gap-2.5 items-center">
        <button className="btn btn-primary" onClick={start} disabled={running || autoRun}>{t('btn.start', language)}</button>
        <button className="btn btn-danger" onClick={stop} disabled={!running || autoRun}>{t('btn.stop', language)}</button>
        <button className="btn" onClick={reset} disabled={autoRun}>{t('btn.reset', language)}</button>
      </div>
      <div className="grid grid-cols-4 gap-2.5 mt-3 max-lg:grid-cols-2 max-sm:grid-cols-1">
        <MetricBox label={t('scroll.fps', language)} value={running ? fps.toFixed(1) : '—'} hint="Target: 60 FPS" />
        <MetricBox label={t('scroll.smoothness', language)} value={running ? q.label : '—'} />
        <MetricBox label="Scroll events" value={String(scrollCount)} />
        <MetricBox label={t('scroll.overscroll', language)} value={'overscrollBehavior' in document.body.style ? t('supported.yes', language) : t('supported.no', language)} />
      </div>
      <div className="h-px bg-border my-3.5" />
      <div
        ref={containerRef}
        onScroll={onScroll}
        className="h-[50vh] min-h-[300px] overflow-auto border border-border rounded-2xl bg-black/20"
        style={{ overscrollBehavior: 'contain', scrollSnapType: 'y proximity' }}
      >
        {items.map((item) => (
          <div
            key={item.id}
            className="p-3 border-b border-white/5 flex items-center gap-3"
            style={{ scrollSnapAlign: 'start' }}
          >
            <div className="w-10 h-10 rounded-xl shadow-lg shrink-0" style={{ background: `linear-gradient(135deg, ${item.color}, ${item.color}88)` }} />
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm truncate">{item.title}</div>
              <div className="text-xs text-muted truncate">{item.desc}</div>
            </div>
            <div className="text-xs text-muted font-mono">#{item.id}</div>
          </div>
        ))}
      </div>
    </TestCard>
  );
}
